import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { AnimatedModal } from "@/src/components/AnimatedModal";
import { Dialog } from "@headlessui/react";
import { toasts } from "@/src/components/toasts/toasts";
import { useHistory, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { DdqlQuery, useDeleteDdqlMutation } from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";

export function DeleteDDQLModal({
  open,
  onClose,
  ddql,
  parentDdqlId,
}: {
  open: boolean;
  onClose: () => void;
  ddql: DdqlQuery["ddql"];
  parentDdqlId?: string;
}) {
  const client = useGqlClient();
  const deleteDdql = useDeleteDdqlMutation(client);
  const history = useHistory();
  const queryClient = useQueryClient();
  const { dealId } = useParams<{ dealId: string }>();
  return (
    <AnimatedModal open={open} onClose={onClose} size="md">
      <div>
        <div className="sm:flex sm:items-start">
          <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
            <ExclamationTriangleIcon
              className="h-6 w-6 text-red-600"
              aria-hidden="true"
            />
          </div>
          <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {`Delete ${ddql.name}`}
            </Dialog.Title>
            <div className="mt-2">
              <p className="text-sm text-gray-500">
                Are you sure you want to delete this question list? This action
                cannot be undone and all questions within this list will be
                permanently removed.
              </p>
            </div>
          </div>
        </div>
        <div className="mt-8 sm:mt-12 sm:flex sm:flex-row-reverse">
          <button
            type="button"
            className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
            onClick={() => {
              deleteDdql.mutate(
                {
                  id: ddql.id,
                },
                {
                  onSuccess: () => {
                    queryClient.invalidateQueries({
                      queryKey: ["Ddql"],
                    });
                    if (dealId) {
                      queryClient.invalidateQueries({
                        queryKey: [
                          "Ddql",
                          { id: parentDdqlId, dealId: dealId },
                        ],
                      });
                    }
                    onClose();
                    toasts.success("DDQL deleted");

                    if (!parentDdqlId) {
                      history.push(`/deal/${dealId}/questions`);
                    }
                  },
                  onError: (e: any) => {
                    toasts.error("Failed to delete DDQL");
                  },
                },
              );
            }}
          >
            {deleteDdql.isPending ? "Deleting..." : "Delete"}
          </button>
          <button
            type="button"
            className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
            onClick={() => {
              onClose();
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </AnimatedModal>
  );
}
